import { z } from 'zod';

// Base Office Schema
export const BaseOfficeSchema = z.object({
  officeNo: z.number().int().positive('Office number must be positive'),
  officeName: z.string().min(1, 'Office name is required').max(100, 'Office name too long'),
  controllerId: z.string().optional(), // Reference to Controller
  regionId: z.number().int().positive('Region ID must be positive'),
  isHeadOffice: z.boolean().default(false),
  isActive: z.boolean().default(true),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional()
});

export type BaseOffice = z.infer<typeof BaseOfficeSchema>;

// Office Creation Schema
export const CreateOfficeSchema = BaseOfficeSchema.omit({
  officeNo: true,
  createdAt: true,
  updatedAt: true
});

export type CreateOffice = z.infer<typeof CreateOfficeSchema>;

// Office Update Schema
export const UpdateOfficeSchema = BaseOfficeSchema.partial().extend({
  officeNo: z.number() // Required for updates
});

export type UpdateOffice = z.infer<typeof UpdateOfficeSchema>;

// Office Response Schema (for API responses)
export const OfficeResponseSchema = BaseOfficeSchema.extend({
  officeNo: z.number()
});

export type OfficeResponse = z.infer<typeof OfficeResponseSchema>;

// Office with Relations Schema
export const OfficeWithRelationsSchema = OfficeResponseSchema.extend({
  region: z.object({
    regionId: z.number(),
    regionName: z.string()
  }).optional(),
  controller: z.object({
    controllerId: z.string(),
    controllerName: z.string()
  }).optional(),
  donorCount: z.number().int().min(0).optional(),
  guestDonorCount: z.number().int().min(0).optional()
});

export type OfficeWithRelations = z.infer<typeof OfficeWithRelationsSchema>;

// Office List Query Schema
export const OfficeListQuerySchema = z.object({
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
  search: z.string().optional(),
  regionId: z.number().int().positive().optional(),
  controllerId: z.string().optional(),
  isActive: z.boolean().optional(),
  isHeadOffice: z.boolean().optional(),
  sortBy: z.enum(['officeNo', 'officeName', 'regionId', 'createdAt']).default('officeNo'),
  sortOrder: z.enum(['asc', 'desc']).default('asc')
});

export type OfficeListQuery = z.infer<typeof OfficeListQuerySchema>;

// Office Assignment Schema
export const OfficeAssignmentSchema = z.object({
  officeNo: z.number(),
  controllerId: z.string().optional(), // null to unassign
  action: z.enum(['assign', 'unassign'])
});

export type OfficeAssignment = z.infer<typeof OfficeAssignmentSchema>;

// Office Statistics Schema
export const OfficeStatisticsSchema = z.object({
  officeNo: z.number(),
  officeName: z.string(),
  regionId: z.number(),
  regionName: z.string(),
  totalDonors: z.number(),
  totalGuestDonors: z.number(),
  totalDonations: z.number(),
  totalAmount: z.number(),
  period: z.object({
    start: z.date(),
    end: z.date()
  })
});

export type OfficeStatistics = z.infer<typeof OfficeStatisticsSchema>;

// Office Range Validation Schema
export const OfficeRangeValidationSchema = z.object({
  regionId: z.number(),
  startOfficeNo: z.number(),
  endOfficeNo: z.number(),
  existingOffices: z.array(z.number())
});

export type OfficeRangeValidation = z.infer<typeof OfficeRangeValidationSchema>;

// Office Number Generation Schema
export const OfficeNumberGenerationSchema = z.object({
  regionId: z.number(),
  requestedCount: z.number().int().positive(),
  startFrom: z.number().int().positive().optional()
});

export type OfficeNumberGeneration = z.infer<typeof OfficeNumberGenerationSchema>; 